import PropTypes from "prop-types";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { FiPlay } from "react-icons/fi";

const specialCharacters = {
  es: ["á", "é", "í", "ó", "ú", "ñ", "ü", "¿", "¡"],
  fr: ["é", "è", "ê", "ë", "à", "â", "ô", "û", "ù", "ï", "ç"],
  de: ["ä", "ö", "ü", "ß"],
  it: ["à", "è", "é", "ì", "ò", "ù"],
  pt: ["á", "â", "ã", "à", "ç", "é", "ê", "í", "ó", "ô", "õ", "ú"],
  nl: ["ij", "á", "é", "í", "ó", "ú"],
};

const VisualWordQuiz = ({
  words: initialWords,
  wordList,
  language,
  languageCode: propLangCode,
  languageInfo,
  isAuthenticated = true,
  signUpPath,
}) => {
  // Support both standalone (baseWords) and word-list mode
  const quizWords = useMemo(() => {
    if (initialWords && initialWords.length > 0) {
      // Standalone mode: BaseWord data
      return initialWords
        .filter((w) => w.translatedText && w.imageUrl)
        .map((w) => ({
          id: w.id,
          englishText: w.englishText,
          targetText: w.translatedText,
          imageUrl: w.imageUrl,
          audioUrl: null,
        }));
    }
    if (wordList && wordList.words) {
      // Word list mode: Word data with base_word images
      return wordList.words
        .filter((w) => w.imageUrl && w.english_text)
        .map((w) => ({
          id: w.id,
          englishText: w.english_text,
          targetText: w.word_text,
          imageUrl: w.imageUrl,
          audioUrl: w.audioWordUrl,
          sentence: w.sentence,
          audioSentenceUrl: w.audioSentenceUrl,
        }));
    }
    return [];
  }, [initialWords, wordList]);

  const languageCode = propLangCode || wordList?.language?.toLowerCase() || "es";

  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [retryMode, setRetryMode] = useState(false);
  const [activeWords, setActiveWords] = useState(quizWords);
  const inputRef = useRef(null);

  useEffect(() => {
    setActiveWords(quizWords);
  }, [quizWords]);

  useEffect(() => {
    if (!showResult && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentIndex, showResult]);

  const currentWord = activeWords[currentIndex];

  if (!activeWords.length) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg mb-2">No visual quiz words available.</p>
        <p className="text-gray-400 text-sm">
          Words need images to use the visual quiz. Add words and wait for images to generate.
        </p>
      </div>
    );
  }

  const handleSpecialCharacter = (char) => {
    setUserAnswer((prev) => prev + char);
    inputRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hasSubmitted || !userAnswer.trim()) return;

    const correct =
      userAnswer.toLowerCase().trim() === currentWord.targetText.toLowerCase().trim();
    setIsCorrect(correct);
    setShowResult(true);
    setHasSubmitted(true);

    if (correct) {
      setCorrectCount((prev) => prev + 1);
      // Award coin
      if (isAuthenticated) {
        try {
          await fetch("/api/v1/user_rewards/add_coin", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content,
            },
          });
        } catch (err) {
          console.error("Error adding coin:", err);
        }
      }
    } else {
      setWrongAnswers((prev) => [...prev, { word: currentWord, userAnswer }]);
    }
  };

  const nextWord = () => {
    if (currentIndex >= activeWords.length - 1) {
      setQuizComplete(true);
      return;
    }
    setCurrentIndex((prev) => prev + 1);
    setUserAnswer("");
    setShowResult(false);
    setHasSubmitted(false);
  };

  const playAudio = (url) => {
    if (!url) return;
    const audio = new Audio(url);
    audio.play().catch((err) => console.error("Audio error:", err));
  };

  const resetQuiz = () => {
    setActiveWords(quizWords);
    setCurrentIndex(0);
    setUserAnswer("");
    setShowResult(false);
    setIsCorrect(false);
    setCorrectCount(0);
    setWrongAnswers([]);
    setHasSubmitted(false);
    setQuizComplete(false);
    setRetryMode(false);
  };

  const retryWrong = () => {
    if (wrongAnswers.length === 0) return;
    setActiveWords(wrongAnswers.map((wa) => wa.word));
    setCurrentIndex(0);
    setUserAnswer("");
    setShowResult(false);
    setIsCorrect(false);
    setCorrectCount(0);
    setWrongAnswers([]);
    setHasSubmitted(false);
    setQuizComplete(false);
    setRetryMode(true);
  };

  // Summary screen
  if (quizComplete) {
    const total = activeWords.length;
    const pct = Math.round((correctCount / total) * 100);

    return (
      <div className="max-w-md mx-auto text-center space-y-6">
        <div className="text-6xl mb-2">
          {pct >= 80 ? "🎉" : pct >= 50 ? "👍" : "💪"}
        </div>
        <h2 className="text-2xl font-bold text-gray-800">Quiz Complete!</h2>
        <p className="text-lg text-gray-600">
          You got <span className="font-bold text-indigo-600">{correctCount}</span> out of{" "}
          <span className="font-bold">{total}</span> correct ({pct}%)
        </p>

        {wrongAnswers.length > 0 && (
          <div className="text-left bg-red-50 rounded-xl p-4 space-y-2">
            <h3 className="font-semibold text-red-800">Words to review:</h3>
            {wrongAnswers.map((wa, i) => (
              <div key={i} className="flex items-center space-x-3 bg-white rounded-lg p-2 border border-red-100">
                {wa.word.imageUrl && (
                  <img src={wa.word.imageUrl} alt="" className="w-10 h-10 rounded object-cover" />
                )}
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{wa.word.englishText}</p>
                  <p className="text-sm">
                    <span className="text-green-600 font-medium">{wa.word.targetText}</span>
                    <span className="text-gray-400 mx-1">·</span>
                    <span className="text-red-500 line-through">{wa.userAnswer}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-3">
          {wrongAnswers.length > 0 && (
            <button
              onClick={retryWrong}
              className="px-6 py-2 bg-yellow-500 text-white rounded-full font-medium hover:bg-yellow-600 transition"
            >
              Retry Wrong Answers
            </button>
          )}
          <button
            onClick={resetQuiz}
            className="px-6 py-2 bg-indigo-600 text-white rounded-full font-medium hover:bg-indigo-700 transition"
          >
            Start Over
          </button>
        </div>

        {!isAuthenticated && (
          <div className="mt-6 bg-indigo-50 rounded-lg p-4">
            <p className="text-sm text-gray-700 mb-2">Sign up to save your progress and earn coins!</p>
            <a
              href={signUpPath}
              className="inline-block px-6 py-2 bg-indigo-600 text-white rounded-full font-medium hover:bg-indigo-700"
            >
              Sign Up Free
            </a>
          </div>
        )}
      </div>
    );
  }

  // Quiz card
  return (
    <div className="max-w-md mx-auto space-y-4">
      {/* Progress Bar */}
      <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
        <span>
          Word {currentIndex + 1} of {activeWords.length}
        </span>
        <span className="text-indigo-600 font-medium">{correctCount} correct</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex + (hasSubmitted ? 1 : 0)) / activeWords.length) * 100}%` }}
        />
      </div>

      {/* Image Card */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        {currentWord.imageUrl && (
          <div className="aspect-square bg-gray-100">
            <img
              src={currentWord.imageUrl}
              alt={currentWord.englishText}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="p-5 space-y-4">
          {/* English word */}
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-1">English</p>
            <p className="text-2xl font-bold text-gray-800">{currentWord.englishText}</p>
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit}>
            <label className="block text-sm font-medium text-gray-600 mb-1 text-center">
              Type the {languageInfo?.name || language} word
            </label>
            <input
              ref={inputRef}
              type="text"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              disabled={hasSubmitted}
              placeholder={`${languageInfo?.name || language} translation...`}
              className={`w-full px-4 py-3 border-2 rounded-xl text-center text-lg focus:outline-none transition ${
                hasSubmitted
                  ? isCorrect
                    ? "border-green-400 bg-green-50"
                    : "border-red-400 bg-red-50"
                  : "border-gray-200 focus:border-indigo-400"
              }`}
              autoComplete="off"
              autoCapitalize="off"
            />

            {/* Special Characters */}
            {specialCharacters[languageCode] && (
              <div className="flex flex-wrap gap-1.5 mt-2 justify-center">
                {specialCharacters[languageCode].map((char, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleSpecialCharacter(char)}
                    disabled={hasSubmitted}
                    className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {char}
                  </button>
                ))}
              </div>
            )}

            {!hasSubmitted && (
              <button
                type="submit"
                disabled={!userAnswer.trim()}
                className="w-full mt-3 px-4 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
              >
                Check Answer
              </button>
            )}
          </form>

          {/* Result */}
          {showResult && (
            <div className={`rounded-xl p-4 ${isCorrect ? "bg-green-50" : "bg-red-50"}`}>
              {isCorrect ? (
                <div className="text-center">
                  <p className="text-lg font-bold text-green-600">Correct! +1 coin</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-lg font-bold text-red-600 mb-1">Not quite!</p>
                  <p className="text-gray-700">
                    The answer is: <span className="font-bold text-green-600">{currentWord.targetText}</span>
                  </p>
                </div>
              )}

              {/* Audio buttons */}
              {currentWord.audioUrl && (
                <div className="flex justify-center mt-3 space-x-2">
                  <button
                    onClick={() => playAudio(currentWord.audioUrl)}
                    className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-200 inline-flex items-center"
                  >
                    <FiPlay className="mr-1" /> Listen
                  </button>
                </div>
              )}

              {/* Sentence */}
              {currentWord.sentence && (
                <p className="text-sm text-gray-600 italic mt-2 text-center">
                  "{currentWord.sentence}"
                </p>
              )}

              <button
                onClick={nextWord}
                className="w-full mt-3 px-4 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition"
              >
                {currentIndex >= activeWords.length - 1 ? "See Results" : "Next Word"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

VisualWordQuiz.propTypes = {
  words: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      englishText: PropTypes.string.isRequired,
      translatedText: PropTypes.string,
      imageUrl: PropTypes.string,
    }),
  ),
  wordList: PropTypes.shape({
    id: PropTypes.number,
    language: PropTypes.string,
    languageName: PropTypes.string,
    words: PropTypes.array,
  }),
  language: PropTypes.string,
  languageCode: PropTypes.string,
  languageInfo: PropTypes.shape({
    flag: PropTypes.string,
    name: PropTypes.string,
  }),
  isAuthenticated: PropTypes.bool,
  signUpPath: PropTypes.string,
};

export default VisualWordQuiz;
